import React from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "../../hooks/useTranslation";
import JourneyBreadcrumb from "../../components/navigation/JourneyBreadcrumb";

const DietitianDashboardPage = () => {
  const { t } = useTranslation();

  const dashboardCards = [
    {
      title: t("Patientprofiler") || "Patientprofiler",
      description: t("Hantera patienter, skapa rätter och planera måltider") || "Hantera patienter, skapa rätter och planera måltider",
      href: "/kostdata/patients",
      icon: "/assets/icons/svg/Profil.svg",
      iconBgColor: "rgba(47, 189, 239, 0.1)",
      iconBgColorHover: "rgba(47, 189, 239, 0.2)",
      iconFilter: "brightness(0) saturate(100%) invert(63%) sepia(81%) saturate(1472%) hue-rotate(166deg) brightness(96%) contrast(91%)",
    },
    {
      title: t("Receptkategorier") || "Receptkategorier",
      description: t("Skapa och hantera kategorier, recept och rätter (Nivå 1)") || "Skapa och hantera kategorier, recept och rätter (Nivå 1)",
      href: "/categories",
      icon: "/assets/icons/svg/Kost.svg",
      iconBgColor: "rgba(52, 149, 100, 0.1)",
      iconBgColorHover: "rgba(52, 149, 100, 0.2)",
      iconFilter: "brightness(0) saturate(100%) invert(49%) sepia(66%) saturate(1818%) hue-rotate(105deg) brightness(95%) contrast(87%)",
    },
    {
      title: t("Måltider") || "Måltider",
      description: t("Visa och hantera alla dina måltider") || "Visa och hantera alla dina måltider",
      href: "/kostdata/meals",
      icon: "/assets/icons/Innehall.png",
      iconBgColor: "rgba(248, 110, 71, 0.1)",
      iconBgColorHover: "rgba(248, 110, 71, 0.2)",
      iconFilter: "brightness(0) saturate(100%) invert(60%) sepia(94%) saturate(1860%) hue-rotate(339deg) brightness(99%) contrast(96%)",
    },
    {
      title: t("Skapa Måltid") || "Skapa Måltid",
      description: t("Skapa en ny måltid") || "Skapa en ny måltid",
      href: "/kostdata/meals/new",
      icon: "/assets/icons/Plus.png",
      iconBgColor: "rgba(52, 149, 100, 0.1)",
      iconBgColorHover: "rgba(52, 149, 100, 0.2)",
      iconFilter: "brightness(0) saturate(100%) invert(49%) sepia(66%) saturate(1818%) hue-rotate(105deg) brightness(95%) contrast(87%)",
    },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-gray-50 to-slate-100">
      <div className="max-w-7xl mx-auto px-4 tablet:px-6 laptop:px-8 desktop:px-8 py-8">
        <JourneyBreadcrumb
          items={[
            { label: t("Dashboard") || "Dashboard" },
          ]}
          className="mb-6"
        />

        {/* Welcome Header */}
        <div className="mb-8 bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
          <h1 className="text-2xl mobile:text-3xl tablet:text-4xl laptop:text-4xl desktop:text-4xl font-bold text-secondary-green mb-2">
            {t("Consupedia Dietist") || "Consupedia Dietist"}
          </h1>
          <p className="text-secondary-green text-base mobile:text-lg">
            {t("Hantera patienter, skapa rätter och planera måltider") || "Hantera patienter, skapa rätter och planera måltider"}
          </p>
        </div>

        {/* Dashboard Cards Grid */}
        <div className="grid grid-cols-1 mobile:grid-cols-1 tablet:grid-cols-2 laptop:grid-cols-2 desktop:grid-cols-2 gap-6 mobile:gap-6 tablet:gap-6 laptop:gap-8 desktop:gap-8">
          {dashboardCards.map((card, index) => (
            <Link
              key={index}
              to={card.href}
              className="group relative bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden transition-all duration-300 transform hover:scale-[1.02] hover:shadow-lg"
            >
              <div className="p-6 mobile:p-8 tablet:p-8 laptop:p-10 desktop:p-10">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex-1">
                    <h3 className="text-xl mobile:text-2xl tablet:text-2xl laptop:text-3xl desktop:text-3xl font-bold text-gray-900 mb-2">
                      {card.title}
                    </h3>
                    <p className="text-gray-600 text-sm mobile:text-base tablet:text-base laptop:text-lg desktop:text-lg">
                      {card.description}
                    </p>
                  </div>
                  <div className="ml-4 flex-shrink-0">
                    <div 
                      className="w-12 h-12 mobile:w-14 mobile:h-14 tablet:w-16 tablet:h-16 laptop:w-16 laptop:h-16 desktop:w-16 desktop:h-16 rounded-xl flex items-center justify-center transition-all duration-300"
                      style={{
                        backgroundColor: card.iconBgColor,
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = card.iconBgColorHover;
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = card.iconBgColor;
                      }}
                    >
                      <img
                        src={card.icon}
                        alt={card.title}
                        className="w-6 h-6 mobile:w-7 mobile:h-7 tablet:w-8 tablet:h-8 laptop:w-8 laptop:h-8 desktop:w-8 desktop:h-8"
                        style={{ filter: card.iconFilter }}
                      />
                    </div>
                  </div>
                </div>
                <div className="flex items-center text-gray-600 group-hover:text-secondary-green transition-colors duration-300">
                  <span className="text-sm mobile:text-base font-semibold mr-2">
                    {t("Öppna") || "Öppna"}
                  </span>
                  <svg
                    className="w-5 h-5 group-hover:translate-x-1 transition-transform duration-300"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 5l7 7-7 7"
                    />
                  </svg>
                </div>
              </div>
            </Link>
          ))}
        </div>

        {/* Quick Stats or Info Section */}
        <div className="mt-8 grid grid-cols-1 mobile:grid-cols-2 tablet:grid-cols-3 laptop:grid-cols-3 desktop:grid-cols-3 gap-4">
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                <img
                  src="/assets/icons/Innehall.png"
                  alt={t("Info")}
                  className="w-6 h-6"
                />
              </div>
              <div>
                <p className="text-sm text-gray-600">{t("Nivå 1") || "Nivå 1"}</p>
                <p className="text-xs text-gray-500">{t("Skapa Rätter") || "Skapa Rätter"}</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                <img
                  src="/assets/icons/Innehall.png"
                  alt={t("Info")}
                  className="w-6 h-6"
                />
              </div>
              <div>
                <p className="text-sm text-gray-600">{t("Nivå 2") || "Nivå 2"}</p>
                <p className="text-xs text-gray-500">{t("Patienter & Måltider") || "Patienter & Måltider"}</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                <img
                  src="/assets/icons/Innehall.png"
                  alt={t("Info")}
                  className="w-6 h-6"
                />
              </div>
              <div>
                <p className="text-sm text-gray-600">{t("Nivå 3") || "Nivå 3"}</p>
                <p className="text-xs text-gray-500">{t("Veckovy (kommer)") || "Veckovy (kommer)"}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DietitianDashboardPage;

